//@flow
import React, {useState} from 'react';
import {Autocomplete} from '@material-ui/lab';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core';
import {useFormContext} from 'react-hook-form';

import CustomButton from '../../../../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../../../../elements/CustomTextField/CustomTextField';
import TextAndBackgroundColorSelector from '../../../../../../../elements/TextAndBackgroundColorSelector';
import {useCategories} from '../../../../../../categories/utils';
import {useStoreDomain} from '../../../../../../store/utils';
import {getImageUrl} from '../../../../../../../utils/image_provider';
import {colors} from '../../../../../../../assets/styles/colors';
import {useStorePromotions} from '../../../../../../marketing/promotions/utils';
import {
  GENERIC_STOREFRONT_PAGES,
  POPUP_BANNER_FIELDS,
  POPUP_BUTTON_ACTION,
} from '../constants';

import type {PopupButtonEntityType} from '../../../../../../../entities';
import {useStoreColorSettings} from '../../../../../../ui/utils';
import {useSearchMinimalProducts} from '../../../../../../products/utils/hooks';
import {debounce} from '../../../../../../../utils/functions';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    gap: '1rem',
  },
  autocompleteContainer: {
    position: 'relative',
    width: '100%',
  },
  autocompleteLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    height: '2.5rem',
    padding: '0.25rem',
    width: '100%',
    overflow: 'hidden',
  },
  autocompleteThumbnail: {
    position: 'relative',
    height: '100%',
    borderRadius: '0.25rem',
    aspectRatio: '1/1',
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  autocompleteData: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    width: '90%',
  },
  autocompleteLabelName: {
    fontWeight: '500',
    fontSize: '0.75rem',
  },
  autocompleteLabelUrl: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.75rem',
    color: colors.primary,
    maxWidth: '100%',
  },
  actionContainer: {
    width: '100%',
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexDirection: 'row !important',
  },
  buttonPreview: {
    maxWidth: '25ch',
    textTransform: 'none',
    fontSize: '0.75rem',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}));

type UrlInputOption = {
  name: string,
  id: string,
  thumbnail?: string,
  url: string,
  typeLabel: 'Categorías' | 'Promociones' | 'Productos' | 'Páginas genéricas',
  type: PopupButtonEntityType,
};

const BUTTON_ACTIONS = [
  {
    label: 'Cerrar Popup',
    value: POPUP_BUTTON_ACTION.CLOSE,
  },
  {
    label: 'Redireccionar',
    value: POPUP_BUTTON_ACTION.REDIRECT,
  },
];

export default function PopupBannerMainButton() {
  const classes = useStyles();

  const categories = useCategories();
  const promotions = useStorePromotions();
  const {domain} = useStoreDomain();

  const colorConfig = useStoreColorSettings();
  const {loading, products, searchProductsByName} = useSearchMinimalProducts();

  const {register, errors, setValue, clearErrors, watch} = useFormContext();

  const [buttonText, setButtonText] = useState('');

  const textColorOptions = [
    {
      hex: '#FFFFFF',
      name: 'Blanco',
    },
    {
      hex: '#3A3A49',
      name: 'Negro',
    },
  ];

  const categoryOption: UrlInputOption[] = categories.map(category => ({
    name: category.name,
    id: category.id,
    thumbnail: getImageUrl(category.portraitImageId),
    url: `https://${domain && domain.domainName}/category/${category.id}`,
    typeLabel: 'Categorías',
    type: 'CATEGORY',
  }));

  const promotionOption: UrlInputOption[] = promotions.map(promotion => ({
    name: promotion.name,
    id: `${promotion.slug}`,
    thumbnail: getImageUrl(promotion.promotionImageId),
    url: `https://${domain && domain.domainName}/promo/${promotion.slug}`,
    typeLabel: 'Promociones',
    type: 'PROMOTION',
  }));

  const productOption: UrlInputOption[] = products.map(product => ({
    name: product.name,
    id: product.id,
    thumbnail: getImageUrl(product.mainImageId),
    url: `https://${domain && domain.domainName}/product/${product.id}`,
    typeLabel: 'Productos',
    type: 'PRODUCT',
  }));

  const genericPagesOption: UrlInputOption[] = GENERIC_STOREFRONT_PAGES.map(
    page => ({
      name: page.label,
      id: page.path,
      url: `https://${domain && domain.domainName}${page.path}`,
      typeLabel: 'Páginas genéricas',
      type: 'URL',
    })
  );

  const handleInputChange = debounce((value: string) => {
    if (!value.trim() && value.length >= 3) return;
    searchProductsByName(value);
  }, 750);

  const buttonAction = watch(POPUP_BANNER_FIELDS.BUTTON_ACTION);

  const showUrlInput = buttonAction === 'REDIRECT';

  return (
    <div className={classes.root}>
      <div className={classes.actionContainer}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Seleccionar función</FormLabel>
          <RadioGroup
            aria-label="Seleccionar función"
            name="buttonAction"
            value={buttonAction}
            className={classes.actionGroup}
            onChange={event => {
              const {value} = event.target;
              setValue(POPUP_BANNER_FIELDS.BUTTON_ACTION, value);
            }}
          >
            {BUTTON_ACTIONS.map(action => (
              <FormControlLabel
                key={action.value}
                value={action.value}
                control={<Radio color="primary" />}
                label={action.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>

      {showUrlInput && (
        <div className={classes.autocompleteContainer}>
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={[
              ...categoryOption,
              ...promotionOption,
              ...productOption,
              ...genericPagesOption,
            ]}
            fullWidth
            freeSolo
            loading={loading}
            autoComplete
            filterOptions={(options, state) => {
              const {inputValue} = state;

              const resultOptions = options.filter(option =>
                option.name.toLowerCase().includes(inputValue.toLowerCase())
              );

              return resultOptions;
            }}
            noOptionsText="No se encontraron resultados"
            getOptionLabel={option => option.url}
            groupBy={option => option.typeLabel}
            onInput={event => {
              const value = event.target.value;
              if (value.includes('https://') || value.includes('http://')) {
                setValue(POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER, value);
                setValue(POPUP_BANNER_FIELDS.BUTTON_URL_TYPE, 'URL');
                clearErrors(POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER);
                return;
              }
              handleInputChange(value);
            }}
            onChange={(event, value) => {
              if (!value) return;

              const isCustomUrl = value.type === 'URL';

              setValue(
                POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER,
                isCustomUrl ? value.url : value.id
              );

              setValue(POPUP_BANNER_FIELDS.BUTTON_URL_TYPE, value.type);
              clearErrors(POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER);
            }}
            renderOption={(option, state) => {
              const {selected} = state;

              return (
                <div
                  className={classes.autocompleteLabel}
                  data-selected={selected}
                >
                  {option.thumbnail && (
                    <picture className={classes.autocompleteThumbnail}>
                      <img src={option.thumbnail} alt="" />
                    </picture>
                  )}
                  <div className={classes.autocompleteData}>
                    <Typography className={classes.autocompleteLabelName}>
                      {option.name}
                    </Typography>
                    <Typography className={classes.autocompleteLabelUrl}>
                      {option.url}
                    </Typography>
                  </div>
                </div>
              );
            }}
            renderInput={params => (
              <CustomTextField
                {...params}
                label=""
                placeholder="Selecciona una URL de redirección"
                type="url"
                fullWidth
                error={!!errors[POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER]}
                helperText={
                  errors[POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER] &&
                  errors[POPUP_BANNER_FIELDS.BUTTON_URL_IDENTIFIER].message
                }
              />
            )}
          />
        </div>
      )}

      <CustomTextField
        label=""
        // value={buttonText}
        onChange={e => setButtonText(e.target.value)}
        placeholder="Texto del botón (Max. 25 caracteres incluyendo espacios)"
        name={POPUP_BANNER_FIELDS.BUTTON_TEXT}
        inputRef={register({
          maxLength: {
            value: 25,
            message: 'Máximo 25 caracteres',
          },
        })}
        fullWidth
        error={!!errors[POPUP_BANNER_FIELDS.BUTTON_TEXT]}
        helperText={
          errors[POPUP_BANNER_FIELDS.BUTTON_TEXT] &&
          errors[POPUP_BANNER_FIELDS.BUTTON_TEXT].message
        }
      />

      <TextAndBackgroundColorSelector
        arrayTextColors={textColorOptions}
        defaultTextColor="#3A3A49"
        defaultBackgroundColor={
          colorConfig && colorConfig.secondaryBackgroundColor
        }
        onChange={(textColor, bgColor) => {
          setValue(POPUP_BANNER_FIELDS.BUTTON_TEXT_COLOR, textColor);
          setValue(POPUP_BANNER_FIELDS.BUTTON_BACKGROUND_COLOR, bgColor);
        }}
        preview={(textColor, bgColor) => (
          <CustomButton
            style={{
              color: textColor,
              backgroundColor: bgColor,
            }}
            className={classes.buttonPreview}
            fullWidth
          >
            {buttonText || 'Botón'}
          </CustomButton>
        )}
      />
    </div>
  );
}
